import { useEffect, useMemo, useRef, useState } from 'react';
import { catalogApiRef, EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';
import { Content, InfoCard } from '@backstage/core-components';
import { Grid, Button, Snackbar, Slide, Typography } from '@material-ui/core';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { entityPageTranslationRef } from '../../translation/entity-page/entity-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { Alert } from '@material-ui/lab';
import { requestPageTranslationRef } from '../../translation/request-page/request-page';

export const RequestForm = () => {
    const { entity } = useEntity();
    const config = useApi(configApiRef);
    const identityApi = useApi(identityApiRef);
    const catalogApi = useApi(catalogApiRef);
    const SYS_INFO_BACKEND_URL = 'backend.baseUrl';
    const backendUrl = config.getString(SYS_INFO_BACKEND_URL);
    const { t } = useTranslationRef(entityPageTranslationRef);
    const translationRequestPage = useTranslationRef(requestPageTranslationRef);
    const submittingRef = useRef(false);

    // Extract projectId from the suffix (after the last '/')
    const rawProject = entity.spec?.project ?? '';
    const projectId =
        typeof rawProject === 'string'
            ? (rawProject.includes('/') ? rawProject.split('/').pop() : rawProject)
            : '';
    const [isUserApprover, setIsUserApprover] = useState<boolean>(false);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
    const [isCheckingAccess, setIsCheckingAccess] = useState<boolean>(true);
    const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean>(false);
    const [usersMap, setUsersMap] = useState<Map<string, string>>(new Map());
    const [appsMap, setAppsMap] = useState<Map<string, string>>(new Map());
    const [isLoadingCatalogData, setIsLoadingCatalogData] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);


    // Estado textual para mostrar en la UI
    const approvalStatus = entity.metadata.annotations?.['backstage.io/approval-status'] ||
        (entity.metadata.tags?.find(tag => tag.startsWith('approval-status:'))?.replace('approval-status:', '') ?? 'unknown');
    const provisioningStatus = entity.metadata.tags?.find(tag => tag.startsWith('provisioning:'))?.replace('provisioning:', '') ?? 'unknown';

    // Detectar si la request ha sido denegada
    const isRequestDenied =
        entity.metadata.annotations?.['backstage.io/approval-status'] === 'denied' ||
        (entity.metadata.tags && entity.metadata.tags.includes('approval-status:denied'));
    // Detectar si la aprobación está pendiente
    const isApprovalPending =
        entity.metadata.annotations?.['backstage.io/approval-status'] === 'pending' ||
        (entity.metadata.tags && entity.metadata.tags.includes('approval:pending')) ||
        (entity.metadata.tags && entity.metadata.tags.includes('approval-status:pending'));

    // Detectar si el provisioning está completado
    const isProvisioningCompleted = entity.metadata.tags?.includes('provisioning:completed');

    // Check user authorization and handle impersonation
    useEffect(() => {
        const checkAccess = async () => {
            console.log('🔄 [Init] Starting access check for request:', entity.metadata.name);
            setIsCheckingAccess(true);

            try {
                const identity = await identityApi.getBackstageIdentity();
                const actualUser = identity.userEntityRef.split('/')[1];
                const userRef = identity.userEntityRef;

                console.log('👤 [User] Actual user from identity:', actualUser);

                // Get actual user's email
                let actualUserEmail = actualUser;
                try {
                    const userEntity = await catalogApi.getEntityByRef(userRef);
                    actualUserEmail = (userEntity?.spec?.profile as any)?.email || actualUser;
                    console.log('📧 [User] Actual user email resolved:', actualUserEmail);
                } catch (err) {
                    console.warn('⚠️ Could not fetch user entity:', err);
                }

                // Check for impersonated user (for development/testing)
                const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
                const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
                const impersonatedEmail = impersonatedFromStorage || impersonatedFromConfig;

                console.log('🎭 [Impersonation] Check:', {
                    fromStorage: impersonatedFromStorage,
                    fromConfig: impersonatedFromConfig,
                    final: impersonatedEmail,
                });

                // Use impersonated user if available, otherwise use actual user
                let effectiveUserName = actualUser;
                let effectiveUserEmail = actualUserEmail;

                // If impersonated, we need to convert email to username
                if (impersonatedEmail && impersonatedEmail !== actualUser) {
                    console.log('🎭 Using impersonated user for RequestForm:', impersonatedEmail);
                    effectiveUserEmail = impersonatedEmail;

                    try {
                        // Query catalog to find user by email
                        const { items } = await catalogApi.getEntities({
                            filter: {
                                kind: 'User',
                                'spec.profile.email': impersonatedEmail,
                            },
                        });

                        if (items.length > 0) {
                            const impersonatedUserName = items[0].metadata.name;
                            effectiveUserName = impersonatedUserName;
                            console.log('🎭 Resolved impersonated email to username:', impersonatedUserName);
                        } else {
                            console.warn('⚠️ Could not find user entity for impersonated email:', impersonatedEmail);
                        }
                    } catch (err) {
                        console.warn('⚠️ Error fetching impersonated user entity:', err);
                    }
                }

                console.log('👤 [Effective User] Using:', {
                    username: effectiveUserName,
                    email: effectiveUserEmail,
                    isImpersonated: effectiveUserEmail !== actualUserEmail,
                });

                // Check if user is platform_admin
                let isPlatformAdminUser = false;
                try {
                    const token = (await identityApi.getCredentials()).token;
                    const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            user: `user:${effectiveUserEmail}`,
                            action: 'platform_admin',
                            resource: 'platform:main',
                        }),
                    });

                    if (response.ok) {
                        const data = await response.json();
                        isPlatformAdminUser = data.allowed || false;
                        setIsPlatformAdmin(isPlatformAdminUser);
                        if (isPlatformAdminUser) {
                            console.log('✅ User is platform_admin - granting full access to RequestForm (can approve/deny)');
                        }
                    }
                } catch (err) {
                    console.warn('⚠️ Error checking platform_admin permission:', err);
                }

                // Get requester and approver from entity
                const requesterName = entity.metadata.annotations?.['example.com/requested-by'] ?? '';
                const approverName = entity.metadata.annotations?.['example.com/approved-by'] ?? '';
                const requestName = entity.metadata.name;

                console.log('🔍 [Debug] Request metadata:', {
                    requestName,
                    requesterName,
                    approverName,
                    effectiveUserName,
                    effectiveUserEmail,
                });

                // Check if user is the requester
                const isRequester = requesterName === effectiveUserName;

                // Check if user can approve using OpenFGA with request tuples
                let canApprove = false;
                let canView = false;
                console.log('🚀 [Debug] Starting OpenFGA request-based checks...');

                try {
                    const token = (await identityApi.getCredentials()).token;

                    // Step 1: Get requester's email and manager
                    const requesterRef = `user:default/${requesterName}`;
                    console.log('🔍 [Debug] Looking up requester entity:', requesterRef);

                    const requesterEntity = await catalogApi.getEntityByRef(requesterRef);
                    const requesterEmail = (requesterEntity?.spec?.profile as any)?.email;

                    console.log('🔍 [Debug] Requester resolved:', {
                        requesterRef,
                        requesterEmail,
                        requesterFound: !!requesterEntity,
                    });

                    if (requesterEmail) {
                        // Step 2: Get manager of the requester from catalog labels
                        const requesterManagerLabel = requesterEntity?.metadata?.labels?.['manager'];
                        console.log('🔍 [Debug] Requester manager label:', requesterManagerLabel);

                        let managerEmail = '';
                        if (requesterManagerLabel) {
                            // Manager label might be username or email, resolve to email
                            try {
                                const managerRef = `user:default/${requesterManagerLabel}`;
                                const managerEntity = await catalogApi.getEntityByRef(managerRef);
                                managerEmail = (managerEntity?.spec?.profile as any)?.email || requesterManagerLabel;
                                console.log('🔍 [Debug] Manager resolved:', {
                                    managerLabel: requesterManagerLabel,
                                    managerEmail,
                                });
                            } catch {
                                // If not found as username, assume it's already an email
                                managerEmail = requesterManagerLabel;
                                console.log('🔍 [Debug] Manager label used as email:', managerEmail);
                            }
                        }

                        // Step 3: Check permissions using contextual tuples
                        // Build contextual tuples for this check
                        const contextualTuples = [];

                        // Always add requester tuple
                        contextualTuples.push({
                            user: `user:${requesterEmail}`,
                            relation: 'requester',
                            object: `request:${requestName}`,
                        });

                        // Add manager tuple if we have a manager
                        if (managerEmail) {
                            contextualTuples.push({
                                user: `user:${managerEmail}`,
                                relation: 'manager',
                                object: `request:${requestName}`,
                            });
                        }

                        console.log('📝 [OpenFGA] Using contextual tuples:', JSON.stringify(contextualTuples, null, 2));

                        // Check can_view permission with contextual tuples
                        console.log(`🔍 [OpenFGA] Checking can_view for user ${effectiveUserEmail} on request ${requestName}`);

                        const canViewCheckPayload = {
                            tuple_key: {
                                user: `user:${effectiveUserEmail}`,
                                relation: 'can_view',
                                object: `request:${requestName}`,
                            },
                            contextual_tuples: {
                                tuple_keys: contextualTuples,
                            },
                        };

                        console.log('🔍 [OpenFGA] can_view check payload:', JSON.stringify(canViewCheckPayload, null, 2));

                        const canViewResponse = await fetch(`${backendUrl}/api/openfga-client/check-permission-contextual`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                            body: JSON.stringify(canViewCheckPayload),
                        });

                        if (canViewResponse.ok) {
                            const canViewData = await canViewResponse.json();
                            canView = canViewData.allowed || false;
                            console.log('🔍 [OpenFGA] can_view check response:', canViewData);
                        } else {
                            console.warn('⚠️ [OpenFGA] can_view check failed:', canViewResponse.status);
                        }

                        // Check can_approve permission with contextual tuples
                        console.log(`🔍 [OpenFGA] Checking can_approve for user ${effectiveUserEmail} on request ${requestName}`);

                        const canApproveCheckPayload = {
                            tuple_key: {
                                user: `user:${effectiveUserEmail}`,
                                relation: 'can_approve',
                                object: `request:${requestName}`,
                            },
                            contextual_tuples: {
                                tuple_keys: contextualTuples,
                            },
                        };

                        console.log('🔍 [OpenFGA] can_approve check payload:', JSON.stringify(canApproveCheckPayload, null, 2));

                        const canApproveResponse = await fetch(`${backendUrl}/api/openfga-client/check-permission-contextual`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                            body: JSON.stringify(canApproveCheckPayload),
                        });

                        if (canApproveResponse.ok) {
                            const canApproveData = await canApproveResponse.json();
                            canApprove = canApproveData.allowed || false;
                            console.log('🔍 [OpenFGA] can_approve check response:', canApproveData);

                            if (canApprove) {
                                console.log('✅ [OpenFGA] User can approve this request (manager or transitive delegate)');
                            } else {
                                console.log('❌ [OpenFGA] User cannot approve this request');
                            }
                        } else {
                            console.warn('⚠️ [OpenFGA] can_approve check failed:', canApproveResponse.status);
                        }
                    } else {
                        console.warn('⚠️ Could not find requester email for:', requesterName);
                        // Fallback to annotation-based check
                        canApprove = approverName === effectiveUserName;
                        canView = isRequester || canApprove;
                        if (canApprove) {
                            console.log('⚠️ [Fallback] Using annotation-based approval check');
                        }
                    }
                } catch (err) {
                    console.error('❌ [Error] Exception during approval permission check:', err);
                    console.error('❌ [Error] Error details:', {
                        message: (err as Error).message,
                        stack: (err as Error).stack,
                    });
                    // Fallback to annotation-based check
                    canApprove = approverName === effectiveUserName;
                    canView = isRequester || canApprove;
                    if (canApprove) {
                        console.log('⚠️ [Fallback] Using annotation-based approval check due to error');
                    } else {
                        console.warn('⚠️ [Fallback] User not authorized via annotation either');
                    }
                }

                console.log('📊 [Summary] Final permissions status:', {
                    canApprove,
                    canView,
                    isRequester,
                    isPlatformAdmin: isPlatformAdminUser,
                });

                setIsUserApprover(canApprove);

                // Check if user is authorized to VIEW the form:
                // - Platform admins can view all requests
                // - Users with can_view permission from OpenFGA (requester or can_approve)
                const authorized = isPlatformAdminUser || canView;

                console.log('📊 [Final Decision] Authorization check:', {
                    authorized,
                    reason: authorized
                        ? (isPlatformAdminUser ? 'platform_admin' : canView ? 'can_view (OpenFGA)' : 'unknown')
                        : 'none',
                    breakdown: {
                        isPlatformAdmin: isPlatformAdminUser,
                        canView,
                        canApprove,
                        isRequester,
                    },
                });

                setIsAuthorized(authorized);

                if (authorized) {
                    if (isPlatformAdminUser) {
                        console.log('✅ [Access] User authorized as platform_admin');
                    } else if (canView) {
                        console.log('✅ [Access] User authorized via OpenFGA can_view (requester or approver)');
                    }
                } else {
                    console.error('🔒 [Access Denied] User not authorized to view RequestForm');
                    console.error('  ❌ User:', effectiveUserName, '/', effectiveUserEmail);
                    console.error('  ❌ Is platform_admin:', isPlatformAdminUser);
                    console.error('  ❌ Can view (OpenFGA):', canView);
                    console.error('  ❌ Can approve (OpenFGA):', canApprove);
                }
            } catch (error) {
                console.error('❌ [Fatal Error] Unexpected error in checkAccess:', error);
                console.error('❌ [Fatal Error] Error details:', {
                    message: (error as Error).message,
                    stack: (error as Error).stack,
                    name: (error as Error).name,
                });
                setIsAuthorized(false);
                setIsUserApprover(false);
            } finally {
                setIsCheckingAccess(false);
            }
        };

        checkAccess();
    }, [projectId, identityApi, catalogApi, backendUrl, config, entity]);

    // Extract relevant fields from the project entity
    const requesterName = entity.metadata.annotations?.['example.com/requested-by'] ?? '';;
    const approverName = entity.metadata.annotations?.['example.com/approved-by'] ?? '';

    const {
        application,
        project,
        component,
    } = entity.spec ?? {};


    // Obtener la annotation para provisioning
    const provisionPayloadRaw = entity.metadata.annotations?.['example.com/provision-payload'];
    let provisionPayload: any = null;
    try {
        if (provisionPayloadRaw) {
            provisionPayload = JSON.parse(provisionPayloadRaw);
        }
    } catch (err) {
        console.error('Error parsing provision-payload:', err);
    }

    // Obtener la annotation para permission-request-payload
    const permissionRequestPayloadRaw = entity.metadata.annotations?.['project-team-management/permission-request-payload'];
    const permissionRequestPayload = useMemo(() => {
        try {
            if (!permissionRequestPayloadRaw) return null;
            return JSON.parse(permissionRequestPayloadRaw);
        } catch (err) {
            console.error('Error parsing permission-request-payload:', err);
            return null;
        }
    }, [permissionRequestPayloadRaw]);


    // 🚨 Detectar si la entidad tiene la tag provisioning:failed
    const hasProvisioningFailed = entity.metadata.tags?.includes('provisioning:failed');

    // 🔥 Estado para el mensaje dinámico
    const [alertInfo, setAlertInfo] = useState<{
        type: 'warning' | 'success' | 'error' | 'info';
        message: string;
    } | null>(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);

    function SlideTransition(props: any) {
        return <Slide {...props} direction="up" />;
    }

    const showAlert = (
        type: 'warning' | 'success' | 'error' | 'info',
        message: string,
    ) => {
        setSnackbarOpen(false);      // cerrar inmediatamente
        setAlertInfo(null);          // resetear estado

        // reabrir en el siguiente ciclo de render
        setTimeout(() => {
            setAlertInfo({ type, message });
            setSnackbarOpen(true);
        }, 50);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleClickButton = async () => {
        if (submittingRef.current) return; // 🔒 lock inmediato
        submittingRef.current = true;
        try {
            setIsSubmitting(true);
            showAlert('info', t('approvalButton.loadingMessage'));

            const entityRef = `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
            const managedByLocation = entity.metadata.annotations?.['backstage.io/managed-by-location'] || '';
            const managedByLocationParts = managedByLocation.split('/');
            const owner = managedByLocationParts[3];
            const repo = managedByLocationParts[4];
            const repoBranch = managedByLocationParts[6];
            const filePath = managedByLocationParts.slice(7).join('/');

            const token = (await identityApi.getCredentials()).token;
            const user = (await identityApi.getBackstageIdentity()).userEntityRef.split('/')[1];

            const response = await fetch(`${backendUrl}/api/devops-api-proxy/approve`, {
                method: 'POST',
                body: JSON.stringify({ entityRef, owner, repo, repoBranch, path: filePath, user }),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            await catalogApi.refreshEntity(entityRef);

            if (response.ok) {
                showAlert('success', t('approvalButton.successMessage'));

                // 🔄 Recargar la página después de 2 segundos
                setTimeout(() => {
                    // if (entity.spec?.type === 'repository') {
                    //     // window.open(window.location.href.replace('/request/', '/component/'), '_self');
                    // } else {
                    window.location.reload();
                    // }
                }, 5000);

            } else {
                showAlert('error', t('approvalButton.errorMessage', {
                    error: data.error || t('approvalButton.unknownError'),
                }));
                setIsSubmitting(false);
            }

        } catch (error) {
            console.error('❌ Error llamando a la API:', error);
            showAlert('error', t('approvalButton.connectionError'));
            setIsSubmitting(false);
            submittingRef.current = false; // liberar en error
        }
    };

    // Nuevo handler para Deny
    const handleDenyButton = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        try {
            setIsSubmitting(true);
            showAlert('info', t('approvalButton.loadingMessage'));

            const entityRef = `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
            const managedByLocation = entity.metadata.annotations?.['backstage.io/managed-by-location'] || '';
            const managedByLocationParts = managedByLocation.split('/');
            const owner = managedByLocationParts[3];
            const repo = managedByLocationParts[4];
            const repoBranch = managedByLocationParts[6];
            const filePath = managedByLocationParts.slice(7).join('/');

            const token = (await identityApi.getCredentials()).token;
            const user = (await identityApi.getBackstageIdentity()).userEntityRef.split('/')[1];

            const response = await fetch(`${backendUrl}/api/devops-api-proxy/deny`, {
                method: 'POST',
                body: JSON.stringify({ entityRef, owner, repo, repoBranch, path: filePath, user }),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            await catalogApi.refreshEntity(entityRef);

            if (response.ok) {
                showAlert('success', t('approvalButton.successMessage'));
                setTimeout(() => {
                    window.location.reload();
                }, 5000);
            } else {
                showAlert('error', t('approvalButton.errorMessage', {
                    error: data.error || t('approvalButton.unknownError'),
                }));
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error('❌ Error llamando a la API:', error);
            showAlert('error', t('approvalButton.connectionError'));
            setIsSubmitting(false);
            submittingRef.current = false;
        }
    };


    useEffect(() => {
        if (!permissionRequestPayload) return;

        const resolveCatalogData = async () => {
            setIsLoadingCatalogData(true);

            try {
                // USERS
                const userEmails = permissionRequestPayload.users || [];

                let usersMapResolved = new Map<string, string>();

                if (userEmails.length > 0) {
                    const usersResp = await catalogApi.getEntities({
                        filter: userEmails.map((email: any) => ({
                            kind: 'User',
                            'spec.profile.email': email,
                        })),
                    });

                    usersMapResolved = new Map(
                        usersResp.items
                            .map((user: any) => {
                                const email = (user.spec as any)?.profile?.email;
                                if (!email) return null;
                                return [email, user.spec?.profile?.displayName ? user.spec?.profile?.displayName : user.metadata.name];
                            })
                            .filter(Boolean) as [string, string][],
                    );
                }

                // APPS
                const appNumbers =
                    permissionRequestPayload.applications?.map((a: any) => a.appNumber) || [];

                let appsMapResolved = new Map<string, string>();

                if (appNumbers.length > 0) {
                    const appsResp = await catalogApi.getEntities({
                        filter: appNumbers.map((num: any) => ({
                            kind: 'Application',
                            'spec.number': num,
                        })),
                    });

                    appsMapResolved = new Map(
                        appsResp.items
                            .map(app => {
                                const number = (app.spec as any)?.number;
                                if (!number) return null;
                                return [number, app.metadata.title ? app.metadata.title : app.metadata.name];
                            })
                            .filter(Boolean) as [string, string][],
                    );
                }

                setUsersMap(usersMapResolved);
                setAppsMap(appsMapResolved);

            } catch (err) {
                console.error('❌ Error resolving catalog data:', err);
            } finally {
                setIsLoadingCatalogData(false);
            }
        };

        resolveCatalogData();
    }, [permissionRequestPayload]);

    // Show loading state while checking access
    if (isCheckingAccess) {
        return (
            <Content noPadding>
                <InfoCard title={translationRequestPage.t('requestForm.title')}>
                    <Typography variant="body1">Loading...</Typography>
                </InfoCard>
            </Content>
        );
    }

    // Show access denied if user is not authorized
    if (!isAuthorized) {
        return (
            <Content noPadding>
                <InfoCard title={translationRequestPage.t('requestForm.title')}>
                    <Alert severity="error">
                        <Typography variant="h6">Access Denied</Typography>
                        <Typography variant="body1">
                            You don't have permission to view this request. Only the requester, approver, or platform administrators can access this page.
                        </Typography>
                    </Alert>
                </InfoCard>
            </Content>
        );
    }

    return (
        <Content noPadding>
            {alertInfo && snackbarOpen && (
                <Snackbar
                    open={snackbarOpen}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    TransitionComponent={SlideTransition}
                >
                    <Alert onClose={handleCloseSnackbar} severity={alertInfo.type} variant="filled">
                        {alertInfo.message}
                    </Alert>
                </Snackbar>
            )}
            <InfoCard title={translationRequestPage.t('requestForm.title')}>
                {/* Estado de aprobación y provisión */}
                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="textSecondary">Approval Status</Typography>
                        <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                            {approvalStatus.charAt(0).toUpperCase() + approvalStatus.slice(1)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="textSecondary">Provisioning Status</Typography>
                        <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                            {provisioningStatus.charAt(0).toUpperCase() + provisioningStatus.slice(1)}
                        </Typography>
                    </Grid>
                </Grid>
                <form>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Grid container spacing={1}>

                                {/* Solicitante */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{translationRequestPage.t('requestTableColumns.requester')}</Typography>
                                    {requesterName ? <EntityRefLink
                                        entityRef={`user:default/${requesterName as string}`}
                                        defaultKind={'User'}
                                        defaultNamespace="default"
                                    /> : <Typography variant="body1">
                                        {requesterName as string || '—'}
                                    </Typography>}
                                </Grid>

                                {/* Approbador */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{translationRequestPage.t('requestTableColumns.approver')}</Typography>
                                    {approverName ? <EntityRefLink
                                        entityRef={`user:default/${approverName as string}`}
                                        defaultKind={'User'}
                                        defaultNamespace="default"
                                    /> : <Typography variant="body1">
                                        {approverName as string || '—'}
                                    </Typography>}
                                </Grid>

                                {/* Project */}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{translationRequestPage.t('requestTableColumns.project')}</Typography>
                                    {project ? <EntityRefLink
                                        entityRef={project as string}
                                        defaultKind={'Project'}
                                        defaultNamespace="default"
                                    /> : <Typography variant="body1">
                                        {project as string || '—'}
                                    </Typography>}
                                </Grid>

                                {/* Applicación */}
                                {entity.spec?.type === 'repository' && <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">{translationRequestPage.t('requestTableColumns.application')}</Typography>
                                    {application ? <EntityRefLink
                                        entityRef={application as string}
                                        defaultKind={'Application'}
                                        defaultNamespace="default"
                                    /> : <Typography variant="body1">
                                        {application as string || '—'}
                                    </Typography>}
                                </Grid>}

                                {/* Requested resource label and value */}
                                <Grid item xs={12} sm={6} style={{ marginTop: entity?.spec?.type === 'repository' ? 15 : 0 }}>
                                    <Typography variant="subtitle2" color="textSecondary">{translationRequestPage.t('requestTableColumns.requestType')}</Typography>
                                    <Typography variant="body1">{typeof entity.spec?.type === 'string' || typeof entity.spec?.type === 'number' ? entity.spec?.type : String(entity.spec?.type || '—')}</Typography>
                                </Grid>

                                {/* Component */}
                                {component && <Grid item xs={12} sm={6} style={{ marginTop: 15 }}>
                                    <Typography variant="subtitle2" color="textSecondary">{translationRequestPage.t('requestTableColumns.component')}</Typography>
                                    {component ? <EntityRefLink
                                        entityRef={component as string}
                                        defaultKind={'Component'}
                                        defaultNamespace="default"
                                    /> : <Typography variant="body1">
                                        {component as string || '—'}
                                    </Typography>}
                                </Grid>}

                                {isLoadingCatalogData ? (
                                    <Typography variant="body2">Resolving data from catalog...</Typography>
                                ) : (
                                    <>
                                        {/* Provisioning Details only if resource is 'repository' */}
                                        {entity.spec?.type === 'repository' && (
                                            <>
                                                <Grid item xs={12} style={{ marginTop: 15 }}>
                                                    <Typography variant="h6">{translationRequestPage.t('requestForm.details.title')}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography variant="subtitle2" color="textSecondary">{translationRequestPage.t('requestForm.details.repository_template')}</Typography>
                                                    <Typography variant="body1">{provisionPayload?.repositoryTemplate?.template?.name || '—'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography variant="subtitle2" color="textSecondary">{translationRequestPage.t('requestForm.details.version_template')}</Typography>
                                                    <Typography variant="body1">{provisionPayload?.repositoryTemplate?.template?.version || '—'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography variant="subtitle2" color="textSecondary">{translationRequestPage.t('requestForm.details.repository')}</Typography>
                                                    <Typography variant="body1">{provisionPayload?.metadata?.repository || '—'}</Typography>
                                                </Grid>
                                            </>
                                        )}

                                        {entity.spec?.type === 'permission' && permissionRequestPayload.applications && (
                                            <>
                                                <Grid item xs={12} style={{ marginTop: 15 }}>
                                                    <Typography variant="h6">Applications</Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    {permissionRequestPayload.applications.length > 0 ? (
                                                        <ul>
                                                            {permissionRequestPayload.applications.map((app: any) => {
                                                                const name = appsMap.get(app.appNumber);

                                                                return (
                                                                    <li key={app.appNumber}>
                                                                        <Typography variant="body1">
                                                                            {name
                                                                                ? `${app.appNumber} - ${name}`
                                                                                : app.appNumber}
                                                                        </Typography>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    ) : (
                                                        <Typography variant="body1">No applications found</Typography>
                                                    )}
                                                </Grid>
                                            </>
                                        )}

                                        {/* Permission Details only if resource is 'permission' */}
                                        {entity.spec?.type === 'permission' && permissionRequestPayload?.users && (
                                            <>
                                                <Grid item xs={12} style={{ marginTop: 15 }}>
                                                    <Typography variant="h6">Users to be granted</Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    {permissionRequestPayload.users.length > 0 ? (
                                                        <ul>
                                                            {permissionRequestPayload.users.map((email: string) => {
                                                                const name = usersMap.get(email);

                                                                return (
                                                                    <li key={email}>
                                                                        <Typography variant="body1">
                                                                            {name ? `${name} (${email})` : email}
                                                                        </Typography>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    ) : (
                                                        <Typography variant="body1">No users found</Typography>
                                                    )}
                                                </Grid>
                                            </>
                                        )}
                                    </>
                                )}
                            </Grid>
                        </Grid>

                        <Grid item xs={12} style={{ display: 'flex', gap: 16 }}>
                            {!isRequestDenied && (
                                <>
                                    {!isProvisioningCompleted && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleClickButton}
                                            disabled={(!isUserApprover && !isPlatformAdmin) || isProvisioningCompleted || isSubmitting}
                                        >
                                            {hasProvisioningFailed
                                                ? t('approvalButton.buttonProvisioningFailedContent')
                                                : t('approvalButton.buttonContent')}
                                        </Button>
                                    )}
                                    {isApprovalPending && (
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleDenyButton}
                                            disabled={(!isUserApprover && !isPlatformAdmin) || !isApprovalPending || isSubmitting}
                                        >
                                            {t('approvalButton.buttonDenyContent')}
                                        </Button>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </InfoCard>

        </Content>
    );
};